// apps/web/src/lib/api.ts
import type {
  User,
  MyProfile,
  PublicProfile,
  UploadSignResponse,
  OcrResult,
  DirectoryQuery,
  DirectoryResponse,
  AdminListResponse,
  Admin,
  AddAdminInput,
  ProfileSubmit,
  Setting,
} from '@hc-alumni/shared';

const API_BASE = '/api';

function formatApiError(errorData: unknown, status: number): string {
  if (!errorData || typeof errorData !== 'object') {
    return `HTTP ${status}`;
  }
  const data = errorData as Record<string, unknown>;

  if (typeof data.error === 'string') return data.error;
  if (typeof data.message === 'string') return data.message;

  const err = data.error;
  if (err && typeof err === 'object') {
    const z = err as { issues?: Array<{ message?: string; path?: unknown[] }>; message?: string };
    if (Array.isArray(z.issues) && z.issues.length > 0) {
      return z.issues
        .map((i) => {
          const path = Array.isArray(i.path) && i.path.length ? `${i.path.join('.')}: ` : '';
          return `${path}${i.message || '欄位有誤'}`;
        })
        .join('；');
    }
    if (typeof z.message === 'string') return z.message;
  }

  if (data.success === false && data.error) {
    return formatApiError({ error: data.error }, status);
  }

  return `HTTP ${status}`;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(formatApiError(errorData, res.status));
  }

  return res.json();
}

export type PublicSiteSettings = {
  site_name: string;
  site_description: string;
  site_tagline: string;
  contact_address: string;
  contact_phone: string;
  contact_website_label: string;
  contact_website_url: string;
  footer_copyright: string;
  show_contact_address?: string;
  show_contact_phone?: string;
};

export type SaveCardResult = {
  ok: boolean;
  isVerified?: boolean;
  needsReview?: boolean;
};

export const api = {
  getPublicSettings: () => request<PublicSiteSettings>('/settings/public'),

  auth: {
    getLineUrl: () => request<{ url: string }>('/auth/line/url'),
    lineCallback: (code: string, state: string) =>
      request<{ user: User; redirect: string }>('/auth/line/callback', {
        method: 'POST',
        body: JSON.stringify({ code, state }),
      }),
    logout: () => request('/auth/logout', { method: 'POST' }),
    me: () => request<{ user: User | null }>('/auth/me'),
  },

  profile: {
    getMe: () => request<MyProfile>('/profile/me'),
    updateMe: (data: Partial<MyProfile>) =>
      request('/profile/me', { method: 'PATCH', body: JSON.stringify(data) }),
  },

  card: {
    getUploadUrl: (filename: string, contentType: string, forUserId?: string) =>
      request<UploadSignResponse>('/card/upload/sign', {
        method: 'POST',
        body: JSON.stringify({
          filename,
          contentType,
          ...(forUserId ? { forUserId } : {}),
        }),
      }),
    ocr: (publicUrl: string, forUserId?: string) =>
      request<OcrResult>('/card/ocr', {
        method: 'POST',
        body: JSON.stringify({
          publicUrl,
          ...(forUserId ? { forUserId } : {}),
        }),
      }),
    save: (data: ProfileSubmit) =>
      request<SaveCardResult>('/card/save', { method: 'PATCH', body: JSON.stringify(data) }),
  },

  directory: {
    list: (params: Partial<DirectoryQuery> = {}) => {
      const searchParams = new URLSearchParams();
      Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
      return request<DirectoryResponse>(`/directory?${searchParams.toString()}`);
    },
    get: (id: string) => request<PublicProfile>(`/directory/${id}`),
  },

  admin: {
    listAlumni: (
      params: {
        page?: number;
        limit?: number;
        status?: 'all' | 'pending' | 'verified';
        q?: string;
      } = {}
    ) => {
      const sp = new URLSearchParams();
      if (params.page) sp.set('page', String(params.page));
      if (params.limit) sp.set('limit', String(params.limit));
      if (params.status) sp.set('status', params.status);
      if (params.q) sp.set('q', params.q);
      return request<
        AdminListResponse & {
          stats?: { total: number; pending: number; verified: number };
        }
      >(`/admin/alumni?${sp.toString()}`);
    },
    listPending: (page = 1) =>
      request<AdminListResponse>(`/admin/alumni?status=pending&page=${page}`),
    getAlumni: (id: string) =>
      request<
        MyProfile & {
          id: string;
          isVerified: boolean;
          createdAt?: number;
          updatedAt?: number;
        }
      >(`/admin/alumni/${id}`),
    updateAlumni: (
      id: string,
      data: Partial<ProfileSubmit> & { keepVerified?: boolean }
    ) =>
      request<SaveCardResult>(`/admin/alumni/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    verify: (id: string, verified: boolean, opts?: { setPrivate?: boolean }) =>
      request(`/admin/verify/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ verified, setPrivate: opts?.setPrivate }),
      }),
    deleteAlumni: (id: string) =>
      request<{ ok: boolean; deletedId: string; name: string }>(`/admin/alumni/${id}`, {
        method: 'DELETE',
      }),
    exportCsv: () => fetch('/api/admin/export', { credentials: 'include' }).then((r) => r.blob()),
    getAdmins: () => request<Admin[]>('/admin/admins'),
    /** 從會員指派主／副管理者（需主管理者） */
    addAdmin: (data: AddAdminInput) =>
      request<Admin>('/admin/admins', { method: 'POST', body: JSON.stringify(data) }),
    removeAdmin: (id: string) => request(`/admin/admins/${id}`, { method: 'DELETE' }),
    getSettings: () => request<Setting[]>('/admin/settings'),
    saveSetting: (key: string, value: string) =>
      request('/admin/settings', { method: 'POST', body: JSON.stringify({ key, value }) }),
    testTelegram: () =>
      request<{ ok: boolean }>('/admin/notify/telegram-test', { method: 'POST' }),
  },
};
